/**
 * AdminTokenTable — Searchable, filterable token table for admin management.
 *
 * Columns: Name+Symbol, Creator, Verification Status, Created, Actions.
 * Search debounced 300ms, verification filter dropdown, contextual action buttons.
 *
 * Story 7.3 — Task 4
 */

import type { Token, VerificationStatus } from "@repo/shared-types";
import { useCallback, useEffect, useRef, useState } from "react";

import { formatRelativeTime } from "../../lib/format-number";

type AdminTokenTableProps = {
	tokens: Token[];
	isLoading: boolean;
	hasMore: boolean;
	onLoadMore: () => void;
	onVerify: (tokenId: string, action: "verify" | "flag" | "remove") => void;
	search: string;
	onSearchChange: (value: string) => void;
	verification: string;
	onVerificationChange: (value: string) => void;
};

const STATUS_BADGE: Record<VerificationStatus, { label: string; className: string }> = {
	verified: { label: "Verified", className: "bg-green-100 text-green-800" },
	flagged: { label: "Flagged", className: "bg-yellow-100 text-yellow-800" },
	unverified: { label: "Unverified", className: "bg-gray-100 text-gray-600" },
	removed: { label: "Removed", className: "bg-red-100 text-red-800" },
};

function getAvailableActions(status: VerificationStatus): Array<"verify" | "flag" | "remove"> {
	switch (status) {
		case "unverified":
			return ["verify", "flag"];
		case "verified":
			return ["flag", "remove"];
		case "flagged":
			return ["verify", "remove"];
		case "removed":
			return ["verify"];
		default:
			return [];
	}
}

function truncateAddress(address: string): string {
	if (address.length <= 10) return address;
	return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

const ACTION_STYLES: Record<string, string> = {
	verify: "bg-green-50 text-green-700 hover:bg-green-100 border-green-200",
	flag: "bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-yellow-200",
	remove: "bg-red-50 text-red-700 hover:bg-red-100 border-red-200",
};

const ACTION_LABELS: Record<string, string> = {
	verify: "Verify",
	flag: "Flag",
	remove: "Remove",
};

const VERIFICATION_OPTIONS = [
	{ value: "", label: "All Statuses" },
	{ value: "unverified", label: "Unverified" },
	{ value: "verified", label: "Verified" },
	{ value: "flagged", label: "Flagged" },
	{ value: "removed", label: "Removed" },
];

function SkeletonRows() {
	return (
		<>
			{Array.from({ length: 5 }).map((_, i) => (
				<tr key={`skeleton-row-${String(i)}`}>
					{Array.from({ length: 5 }).map((__, j) => (
						<td key={`skeleton-cell-${String(i)}-${String(j)}`} className="px-4 py-3">
							<div className="h-4 w-full animate-pulse rounded bg-pastel-light-gray" />
						</td>
					))}
				</tr>
			))}
		</>
	);
}

export function AdminTokenTable({
	tokens,
	isLoading,
	hasMore,
	onLoadMore,
	onVerify,
	search,
	onSearchChange,
	verification,
	onVerificationChange,
}: AdminTokenTableProps) {
	const [searchInput, setSearchInput] = useState(search);
	const isFirstRender = useRef(true);

	// Sync external search changes
	useEffect(() => {
		setSearchInput(search);
	}, [search]);

	// Debounce search input (M4: skip firing on initial mount)
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}
		const timer = setTimeout(() => {
			onSearchChange(searchInput);
		}, 300);
		return () => clearTimeout(timer);
	}, [searchInput, onSearchChange]);

	const handleSearchInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchInput(e.target.value);
	}, []);

	return (
		<div>
			{/* Controls */}
			<div className="mb-4 flex flex-col gap-3 sm:flex-row">
				<input
					type="text"
					placeholder="Search by name or symbol..."
					aria-label="Search tokens"
					value={searchInput}
					onChange={handleSearchInput}
					className="flex-1 rounded-lg border border-pastel-border bg-pastel-white px-4 py-2 text-sm text-pastel-black placeholder:text-pastel-gray focus:outline-none focus:ring-2 focus:ring-pastel-vibrant-blue/40"
				/>
				<select
					value={verification}
					aria-label="Filter by verification status"
					onChange={(e) => onVerificationChange(e.target.value)}
					className="rounded-lg border border-pastel-border bg-pastel-white px-4 py-2 text-sm text-pastel-black focus:outline-none focus:ring-2 focus:ring-pastel-vibrant-blue/40"
				>
					{VERIFICATION_OPTIONS.map((opt) => (
						<option key={opt.value} value={opt.value}>
							{opt.label}
						</option>
					))}
				</select>
			</div>

			{/* Table */}
			<div className="overflow-x-auto rounded-lg border border-pastel-border">
				<table className="w-full text-left text-sm">
					<thead className="border-b border-pastel-border bg-pastel-light-gray">
						<tr>
							<th className="px-4 py-3 font-medium text-pastel-gray">Token</th>
							<th className="px-4 py-3 font-medium text-pastel-gray">Creator</th>
							<th className="px-4 py-3 font-medium text-pastel-gray">Status</th>
							<th className="px-4 py-3 font-medium text-pastel-gray">Created</th>
							<th className="px-4 py-3 font-medium text-pastel-gray">Actions</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-pastel-border bg-pastel-white">
						{isLoading ? (
							<SkeletonRows />
						) : tokens.length === 0 ? (
							<tr>
								<td colSpan={5} className="px-4 py-8 text-center text-pastel-gray">
									No tokens found
								</td>
							</tr>
						) : (
							tokens.map((token) => {
								const badge = STATUS_BADGE[token.verificationStatus];
								const actions = getAvailableActions(token.verificationStatus);

								return (
									<tr key={token.id} className="hover:bg-pastel-light-gray/50">
										<td className="px-4 py-3">
											<span className="font-medium text-pastel-black">{token.name}</span>
											<span className="ml-1 text-pastel-gray">{token.symbol}</span>
										</td>
										<td className="px-4 py-3 font-mono text-xs text-pastel-gray">
											{truncateAddress(token.creatorWallet)}
										</td>
										<td className="px-4 py-3">
											<span
												className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${badge.className}`}
											>
												{badge.label}
											</span>
										</td>
										<td className="px-4 py-3 text-pastel-gray" title={token.createdAt}>
											{formatRelativeTime(token.createdAt)}
										</td>
										<td className="px-4 py-3">
											<div className="flex gap-1">
												{actions.map((action) => (
													<button
														key={action}
														type="button"
														aria-label={`${ACTION_LABELS[action]} ${token.name}`}
														onClick={() => onVerify(token.id, action)}
														className={`rounded border px-2 py-1 text-xs font-medium transition-colors ${ACTION_STYLES[action]}`}
													>
														{ACTION_LABELS[action]}
													</button>
												))}
											</div>
										</td>
									</tr>
								);
							})
						)}
					</tbody>
				</table>
			</div>

			{/* Load more */}
			{hasMore && !isLoading && (
				<div className="mt-4 text-center">
					<button
						type="button"
						onClick={onLoadMore}
						className="rounded-lg border border-pastel-border bg-pastel-white px-6 py-2 text-sm font-medium text-pastel-black transition-colors hover:bg-pastel-light-gray"
					>
						Load More
					</button>
				</div>
			)}
		</div>
	);
}
